import { AppLayout } from "@bcc/components/app-layout";
import { getBatchSummaryProducts, setBatchSummaryDialogTestId } from "@bcc/components/batches/batch-summary-dialog.utils";
import { getReadableBatchId } from "@bcc/components/batches/batch-summary.utils";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { useBatchDetails } from "@bcc/hooks/use-batch-details";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { useConfigState } from "@client-shared/providers/config";
import type { PurchaseOrder, PurchaseOrderLine } from "@shared/types";
import { ArrowLeft, Printer } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const ERROR_STATUSES = new Set(["LabelError", "Error"]);

const getCustomerName = (purchaseOrder: PurchaseOrder): string => {
  const { shippingAddress } = purchaseOrder;
  if (!shippingAddress) return "—";
  if (shippingAddress.name) return shippingAddress.name;
  const fullName = [shippingAddress.firstName, shippingAddress.lastName].filter(Boolean).join(" ").trim();
  return fullName || "—";
};

const getUniqueTrackingNumbers = (lineItems: PurchaseOrderLine[]): string[] => {
  const seen = new Set<string>();
  for (const { trackingNumber } of lineItems) {
    if (trackingNumber) seen.add(trackingNumber);
  }
  return Array.from(seen);
};

export function BatchSummaryPage() {
  const { t } = useTranslation();
  const [, , configUtils] = useConfigState();
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();
  const { batch, isLoading } = useBatchDetails(batchId);

  const products = useMemo(() => getBatchSummaryProducts(batch), [batch]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handleBack = useCallback(() => {
    navigate({ pathname: ROUTE_PATHS.BATCHES, search: `?batchId=${batchId}` });
  }, [navigate, batchId]);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-sm text-muted-foreground">
          {t("dataTable.loading")}
        </div>
      );
    }

    if (!batch) {
      return (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-sm text-muted-foreground">
          {t("views.batchDetailModal.description")}
        </div>
      );
    }

    const readableId = getReadableBatchId(batch.batchId);
    const hasTotalWeight = batch.totalWeightInGrams !== undefined;

    return (
      <Card className="border-primary/20 print:border-0 print:shadow-none" testId={setBatchSummaryDialogTestId("container")}>
        <CardContent className="space-y-6 p-6 print:p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3" data-testid={setBatchSummaryDialogTestId("title")}>
              <h1 className="text-2xl font-semibold tracking-tight">{t("views.batchSummaryDialog.title")}</h1>
              <Badge variant="secondary">{readableId}</Badge>
            </div>
            <Button variant="outline" onClick={handlePrint} data-testid={setBatchSummaryDialogTestId("print")} className="print:hidden">
              <Printer className="mr-2 h-4 w-4" />
              {t("views.batchSummaryDialog.print")}
            </Button>
          </div>

          <section
            className="grid grid-cols-2 gap-4 rounded-lg border border-border/60 bg-muted/30 p-4 sm:grid-cols-4"
            data-testid={setBatchSummaryDialogTestId("metrics")}
          >
            <MetricRow label={t("views.batchSummary.totalOrders")} value={batch.totalOrders} />
            <MetricRow label={t("views.batchSummary.totalUnits")} value={batch.totalUnits} />
            <MetricRow
              label={t("views.batchSummary.totalWeight")}
              value={
                hasTotalWeight
                  ? t("weight.weightWithSuffix", {
                      grams: batch.totalWeightInGrams ?? 0,
                    })
                  : "—"
              }
            />
            <MetricRow label={t("views.batchSummary.labelsPrinted")} value={batch.labelCreatedOrders} />
          </section>

          <section data-testid={setBatchSummaryDialogTestId("pick-list")} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {t("views.batchSummaryDialog.pickList")}
            </h2>
            {products.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("views.batchSummaryDialog.noProducts")}</p>
            ) : (
              <ul className="space-y-4">
                {products.map((product) => (
                  <li
                    key={product.title ?? ""}
                    className="rounded-md border border-border/60 p-3"
                    data-testid={setBatchSummaryDialogTestId(`product-${product.title ?? "unknown"}`)}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium text-sm">{product.title ?? "—"}</span>
                      <span className="text-sm text-muted-foreground">
                        {
                          t("intlNumber", {
                            value: product.totalUnits,
                          }) as string
                        }{" "}
                        {t("views.batchSummaryDialog.unitsLabel")} ·{" "}
                        {t("weight.weightWithSuffix", {
                          grams: product.totalWeight,
                        })}
                      </span>
                    </div>
                    {product.grinds.length > 0 && (
                      <ul className="mt-2 space-y-1 pl-3 text-xs text-muted-foreground">
                        {product.grinds.map((grind) => (
                          <li key={grind.grindType} className="flex items-baseline justify-between gap-2">
                            <span className="inline-flex items-center gap-2">
                              <Badge variant="outline">
                                {configUtils?.findTranslatedValue("grind", grind.grindType) ?? grind.grindType}
                              </Badge>
                              <span>
                                {
                                  t("intlNumber", {
                                    value: grind.totalUnits,
                                  }) as string
                                }{" "}
                                {t("views.batchSummaryDialog.unitsLabel")}
                              </span>
                            </span>
                            <span>
                              {t("weight.weightWithSuffix", {
                                grams: grind.totalWeight,
                              })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section data-testid={setBatchSummaryDialogTestId("orders")} className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {t("views.batchSummaryDialog.orders")}
            </h2>
            {batch.purchaseOrders.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("views.batchSummaryDialog.noOrders")}</p>
            ) : (
              <ul className="space-y-3">
                {batch.purchaseOrders.map((purchaseOrder) => {
                  const trackingNumbers = getUniqueTrackingNumbers(purchaseOrder.lineItems);
                  const isErrored = ERROR_STATUSES.has(`${purchaseOrder.orderStatus}`);
                  const customerName = getCustomerName(purchaseOrder);
                  return (
                    <li
                      key={purchaseOrder.purchaseOrderId}
                      className="rounded-md border border-border/60 p-3 print:break-inside-avoid"
                      data-testid={setBatchSummaryDialogTestId(`order-${purchaseOrder.purchaseOrderId}`)}
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div className="flex flex-wrap items-baseline gap-2">
                          {isErrored && purchaseOrder.orderStatus && <Badge variant="destructive">{purchaseOrder.orderStatus}</Badge>}
                          <span className="font-medium text-sm">{purchaseOrder.purchaseOrderId}</span>
                          <span className="text-sm text-muted-foreground">{customerName}</span>
                        </div>
                        {trackingNumbers.length > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {t("views.batchSummaryDialog.tracking")}: {trackingNumbers.join(", ")}
                          </span>
                        )}
                      </div>
                      {purchaseOrder.lineItems.length > 0 && (
                        <ul className="mt-2 space-y-1 pl-3 text-xs text-muted-foreground">
                          {purchaseOrder.lineItems.map((lineItem) => (
                            <li
                              key={lineItem.id ?? `${lineItem.productTitle}-${lineItem.grindType ?? ""}`}
                              className="flex flex-wrap items-baseline justify-between gap-2"
                            >
                              <span className="inline-flex items-baseline gap-2">
                                <span className="font-medium text-foreground">{lineItem.quantity}×</span>
                                <span>{lineItem.productTitle ?? "—"}</span>
                                {lineItem.grindType && (
                                  <Badge variant="outline">
                                    {configUtils?.findTranslatedValue("grind", lineItem.grindType) ?? lineItem.grindType}
                                  </Badge>
                                )}
                              </span>
                              <span>
                                {t("weight.weightWithSuffix", {
                                  grams: lineItem.lineItemWeightInGrams ?? 0,
                                })}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </CardContent>
      </Card>
    );
  };

  return (
    <AppLayout>
      <div className="space-y-4">
        <Button variant="ghost" onClick={handleBack} className="print:hidden" data-testid={setBatchSummaryDialogTestId("back")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("buttons.back")}
        </Button>
        {renderBody()}
      </div>
    </AppLayout>
  );
}

interface MetricRowProps {
  readonly label: string;
  readonly value: string | number;
}

function MetricRow({ label, value }: MetricRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold">{value}</span>
    </div>
  );
}
